/**
 * TodayRoute — /i-dag (Plan 03-09 D-12).
 *
 * Displays:
 *   - Display S header: "I dag, fredag 8. mai" (formatDayHeader) + WeekChip (uke N)
 *   - Hendelser section: today's events as full EventCards (all-day floated to top)
 *   - Oppgaver section: today chore count + "Til Oppgavebunken →" link
 *
 * Both sections reuse shared hooks (useTodayEvents shared with EventsTile per D-12).
 *
 * T-CAL-43: /i-dag is authenticated; 401 falls through to LoginRoute via app-level
 * session guard (existing Phase 1 guard).
 */
import { useTranslation } from 'react-i18next';
import { WeekChip } from '../../components/primitives';
import { useTodayEvents } from './useTodayEvents';
import { TodayEventList } from './TodayEventList';
import { TodayChoreSummaryLink } from './TodayChoreSummaryLink';
import { formatDayHeader, formatWeekNumber } from '../../i18n/formatters';

export function TodayRoute() {
  const { t } = useTranslation();
  const { data: events = [] } = useTodayEvents();
  const today = new Date();
  const isoWeek = Number(formatWeekNumber(today));
  const dayHeader = formatDayHeader(today);

  return (
    <main
      aria-label={t('nav.calendar')}
      data-testid="today-route"
      className="p-5 sm:p-12 min-h-screen"
    >
      <header className="flex items-baseline gap-3 mb-6">
        <h1
          className="text-display-s font-semibold font-display tracking-tight"
          data-testid="today-header"
        >
          {t('calendar.today.heading', { date: dayHeader })}
        </h1>
        <WeekChip number={isoWeek} />
      </header>

      <section
        aria-label={t('calendar.today.sectionEvents')}
        data-testid="today-events"
        className="mb-8"
      >
        <h2 className="text-label font-semibold uppercase tracking-widest text-ink-2 mb-3">
          {t('calendar.today.sectionEvents')} · <span className="tnum">{events.length}</span>
        </h2>
        <TodayEventList events={events} />
      </section>

      <TodayChoreSummaryLink />
    </main>
  );
}
